import { useState, useEffect } from 'react';
import { ArrowDownLeft, ArrowUpRight, ArrowLeftRight } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface Category {
  id: string;
  name: string;
  type: string;
}

const inputClass = 'border-slate-200 text-slate-900 placeholder:text-slate-400 focus-visible:ring-slate-900/20 focus-visible:border-slate-400 rounded-lg h-10';
const labelClass = 'text-slate-900 font-medium';

interface AddTransactionModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export default function AddTransactionModal({ open, onClose, onSuccess }: AddTransactionModalProps) {
  const [categories, setCategories] = useState<Category[]>([]);
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [type, setType] = useState<'income' | 'expense' | 'transfer'>('expense');
  const [categoryId, setCategoryId] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      supabase.from('categories').select('id, name, type').eq('type', type).then(({ data }) => setCategories(data || []));
      setCategoryId('');
    }
  }, [open, type]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    if (!description.trim()) {
      setError('Description is required');
      return;
    }
    const amt = Number(amount);
    if (isNaN(amt) || amt === 0) {
      setError('Enter a valid amount');
      return;
    }

    const sign = type === 'expense' ? -1 : 1;
    const absAmount = Math.abs(amt) * sign;

    setIsSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data: profile } = await supabase.from('profiles').select('currency').eq('id', user.id).single();
      const currency = profile?.currency || 'USD';

      const { error: insertError } = await supabase.from('transactions').insert({
        user_id: user.id,
        category_id: categoryId || null,
        date,
        description: description.trim(),
        amount: absAmount,
        type,
        currency,
      });

      if (insertError) throw insertError;

      setDate(new Date().toISOString().split('T')[0]);
      setDescription('');
      setAmount('');
      setCategoryId('');
      onSuccess();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add transaction');
    } finally {
      setIsSubmitting(false);
    }
  };

  const typeOptions = [
    { value: 'expense' as const, label: 'Expense', icon: ArrowDownLeft },
    { value: 'income' as const, label: 'Income', icon: ArrowUpRight },
    { value: 'transfer' as const, label: 'Transfer', icon: ArrowLeftRight },
  ];

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="border-slate-200 rounded-2xl bg-white shadow-xl max-w-md">
        <DialogHeader>
          <DialogTitle className="text-slate-900 text-xl">Add transaction</DialogTitle>
          <DialogDescription className="text-slate-600">
            Record income, an expense, or a transfer.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label className={labelClass}>Type</Label>
            <div className="flex gap-2 p-1 rounded-lg bg-slate-100 border border-slate-200">
              {typeOptions.map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setType(value)}
                  className={`flex-1 flex items-center justify-center gap-2 rounded-md py-2.5 text-sm font-medium transition-colors ${
                    type === value ? 'bg-white text-slate-900 shadow-sm border border-slate-200' : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tx-date" className={labelClass}>Date</Label>
              <Input id="tx-date" type="date" className={inputClass} value={date} onChange={(e) => setDate(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="tx-amount" className={labelClass}>Amount</Label>
              <Input id="tx-amount" type="number" step="0.01" className={inputClass} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tx-description" className={labelClass}>Description</Label>
            <Input id="tx-description" className={inputClass} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="e.g. Grocery shopping" />
          </div>

          <div className="space-y-2">
            <Label className={labelClass}>Category (optional)</Label>
            <Select value={categoryId || 'none'} onValueChange={(v) => setCategoryId(v === 'none' ? '' : v)}>
              <SelectTrigger className={inputClass}>
                <SelectValue placeholder="No category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No category</SelectItem>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-200 bg-red-50 [&>svg]:text-red-600">
              <AlertDescription className="text-red-800">{error}</AlertDescription>
            </Alert>
          )}

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="rounded-full border-slate-300 text-slate-700 hover:bg-slate-50">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="rounded-full bg-slate-900 text-white hover:bg-slate-800 font-medium">
              {isSubmitting ? (
                <>
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent mr-2" />
                  Adding...
                </>
              ) : (
                'Add transaction'
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
